import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, Switch, Grid, Avatar, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, PersonOff, PersonAdd } from '@mui/icons-material';
import { usersAPI } from '../services/api';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  erfasser: 'Erfasser',
  bauhof: 'Bauhof',
  betrachter: 'Betrachter',
};

const ROLE_COLORS: Record<string, any> = {
  admin: 'error',
  erfasser: 'primary',
  bauhof: 'warning',
  betrachter: 'default',
};

interface UserForm {
  email: string;
  password: string;
  name: string;
  role: string;
  phone: string;
}

export default function Users() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<UserForm>({ email: '', password: '', name: '', role: 'erfasser', phone: '' });
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch {
      setError('Benutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const openCreate = () => {
    setForm({ email: '', password: '', name: '', role: 'erfasser', phone: '' });
    setEditId(null);
    setDialog(true);
  };

  const openEdit = (user: any) => {
    setForm({ email: user.email, password: '', name: user.name, role: user.role, phone: user.phone || '' });
    setEditId(user.id);
    setDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const data: any = { ...form };
        if (!data.password) delete data.password;
        await usersAPI.update(editId, data);
        setSuccess('Benutzer aktualisiert');
      } else {
        await usersAPI.create(form);
        setSuccess('Benutzer erstellt');
      }
      setDialog(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId: string) => {
    try {
      await usersAPI.toggleActive(userId);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Status');
    }
  };

  const handleDelete = async (userId: string, name: string) => {
    if (!window.confirm(`Benutzer "${name}" wirklich löschen?`)) return;
    try {
      await usersAPI.delete(userId);
      setSuccess('Benutzer gelöscht');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5">Benutzerverwaltung</Typography>
          <Typography variant="body2" color="text.secondary">{users.length} Benutzer registriert</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Neuer Benutzer
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Benutzer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Letzter Login</TableCell>
              <TableCell>Erstellt</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover sx={{ opacity: user.active ? 1 : 0.5 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: user.active ? 'primary.main' : 'grey.400', width: 36, height: 36, fontSize: '0.85rem' }}>
                      {user.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                      {user.phone && <Typography variant="caption" color="text.secondary">{user.phone}</Typography>}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip label={ROLE_LABELS[user.role] || user.role} size="small" color={ROLE_COLORS[user.role]} />
                </TableCell>
                <TableCell>
                  <Chip label={user.active ? 'Aktiv' : 'Deaktiviert'} size="small"
                    color={user.active ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  {user.last_login ? new Date(user.last_login).toLocaleDateString('de-DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : '–'}
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString('de-DE')}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => openEdit(user)}><Edit /></IconButton>
                  </Tooltip>
                  <Tooltip title={user.active ? 'Deaktivieren' : 'Aktivieren'}>
                    <IconButton size="small" onClick={() => handleToggleActive(user.id)}
                      color={user.active ? 'default' : 'success'}>
                      {user.active ? <PersonOff /> : <PersonAdd />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" color="error" onClick={() => handleDelete(user.id, user.name)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={editId ? 'Neues Passwort (leer = unverändert)' : 'Passwort'}
                type="password" required={!editId} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                helperText="Mindestens 8 Zeichen" />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select value={form.role} label="Rolle"
                  onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="erfasser">Erfasser</MenuItem>
                  <MenuItem value="bauhof">Bauhof</MenuItem>
                  <MenuItem value="betrachter">Betrachter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Telefon (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.name || !form.email || (!editId && !form.password)}>
            {editId ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
